import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';
import headerObject from '@/types/headerObj';
import { GitProvider } from '@/types/providerUser';

const localHost = apiUrl;

export interface CursorTeamMember {
  email: string;
  role?: string;
  name?: string;
  id?: string;
}

export interface CursorSetupTeamMappingRequest {
  org_uuid: string;
  cursor_api_key: string;
  team_uuid?: string;
}

export interface CursorSetupTeamMappingResponse {
  success: boolean;
  message?: string;
  error?: string;
  valid?: boolean;
  configured?: boolean;
  team_members?: CursorTeamMember[];
}

export interface CursorValidateConfigRequest {
  org_uuid: string;
  team_uuid?: string;
}

export interface CursorValidateConfigResponse {
  success: boolean;
  configured: boolean;
  valid?: boolean;
  error?: string;
  team_members?: CursorTeamMember[];
}

export interface DeleteCursorApiKeyRequest {
  org_uuid: string;
}

export interface DeleteCursorApiKeyResponse {
  success: boolean;
  message?: string;
  error?: string;
}

const setupCursorTeamMapping = async (
  request: CursorSetupTeamMappingRequest,
  headerObj?: headerObject
): Promise<CursorSetupTeamMappingResponse | undefined> => {
  try {
    const res = await postWithAuth(
      localHost + 'cursor/setupTeamMapping/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err: any) {
    console.error('Error setting up Cursor team mapping:', err);
    throw err;
  }
};

const validateCursorConfig = async (
  request: CursorValidateConfigRequest,
  headerObj?: headerObject
): Promise<CursorValidateConfigResponse | undefined> => {
  try {
    const res = await postWithAuth(
      localHost + 'cursor/validateConfig/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    const data = (await res.json()) as CursorValidateConfigResponse;
    return {
      ...data,
      valid: data.valid ?? data.configured,
    };
  } catch (err: any) {
    console.error('Error validating Cursor config:', err);
    throw err;
  }
};

const deleteCursorApiKey = async (
  request: DeleteCursorApiKeyRequest,
  headerObj?: headerObject
): Promise<DeleteCursorApiKeyResponse | undefined> => {
  try {
    const res = await postWithAuth(
      localHost + 'cursor/deleteApiKey/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err: any) {
    console.error('Error deleting Cursor API key:', err);
    throw err;
  }
};

// ============================================================================
// Cursor User Mapping
// ============================================================================

export interface CursorUser {
  email: string;
  name: string;
}

export interface BackendUser {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  github_username?: string;
  gitlab_username?: string;
  bitbucket_username?: string;
  is_mapped: boolean;
}

export interface ExistingMapping {
  mapping_id: number;
  cursor_user_email: string;
  backend_username: string;
  backend_user_name?: string;
  platform: GitProvider | string;
}

/**
 * A deep first window so the list fills in one request, then shallower ones as
 * the reader scrolls. The server caps `limit` at 200.
 */
export const CURSOR_USERS_FIRST_LIMIT = 100;
export const CURSOR_USERS_NEXT_LIMIT = 100;

export interface CursorUserMappingRequest {
  org_uuid: string;
  action: 'list' | 'create' | 'delete' | 'update';
  backend_username?: string;
  cursor_user_email?: string;
  platform?: GitProvider;
  mapping_id?: number;
  /** `list` only: offset into the windowed directory. Omit for the legacy full response. */
  offset?: number;
  /** `list` only: window size. Omit for the legacy full response; server clamps to 200. */
  limit?: number;
  /** `list` only: substring match over the tool user's name and email. */
  search?: string;
}

export interface CursorUserMappingResponse {
  cursor_users: CursorUser[];
  backend_users: BackendUser[];
  existing_mappings: ExistingMapping[];
  success: boolean;
  message?: string;
  error?: string;
  cursor_configured?: boolean;
  cursor_org_id?: string | null;
  // Set by the backend while a background auto-mapping sweep is running.
  // Poll the list action until it clears to pick up the new mappings.
  auto_mapping_in_progress?: boolean;
  // Present when the request carried a paging `limit`: count of the filtered
  // directory and whether more rows exist past this window.
  total?: number;
  has_more?: boolean;
}

const manageCursorUserMappings = async (
  request: CursorUserMappingRequest,
  headerObj?: headerObject
): Promise<CursorUserMappingResponse | undefined> => {
  if (!request.org_uuid) {
    console.error('Missing required parameter: org_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(localHost + 'aiUserMapping/', request, {
      headers: {
        ...headerObj,
      },
    });
    return await res.json();
  } catch (err: any) {
    console.error('Error managing cursor user mappings:', err);
    throw err;
  }
};

export {
  setupCursorTeamMapping,
  validateCursorConfig,
  deleteCursorApiKey,
  manageCursorUserMappings,
};
